import { Component, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsItemModel, DetailsModel } from 'src/app/shared/models/details.model'
import { format } from 'date-fns'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'
import {
  ClusteredSignProperties,
  SupplementalSign,
} from 'src/app/shared/models/clustered-sign-model'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { parseIfString } from 'src/app/shared/utils/json.utils'

@Component({
  selector: 'app-clustered-signs-details',
  templateUrl: './clustered-signs-details.component.html',
  standalone: false,
})
export class ClusteredSignsDetailsComponent {
  public readonly selectedFeatureId = signal('')
  public readonly properties = signal<ClusteredSignProperties | undefined>(undefined)
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly zoomLevel = signal<number | undefined>(undefined)
  public readonly index = signal(0)

  constructor(
    private readonly mapEventService: MapEventsService,
    private appState: AppStateService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        this.clearDetails()
        const index = this.features().length !== features.length ? 0 : this.index()
        const selectedFeature = features[index]
        if (selectedFeature?.layer.id === Layers.ClusteredSigns.name) {
          this.features.set(features)
          this.index.set(index)
          this.setDetails()
        }
      })
  }

  toggleFeature(index: number) {
    this.index.set(index)
    this.setDetails()
  }

  setDetails() {
    const feature = this.features()[this.index()]
    if (!feature) return

    const selectedFeatureId = feature.id?.toString() ?? ''
    this.selectedFeatureId.set(selectedFeatureId)
    const raw = feature.properties as ClusteredSignProperties
    const properties: ClusteredSignProperties = {
      ...raw,
      supplementalSign: parseIfString(raw.supplementalSign),
      tags: parseIfString(raw.tags),
    }
    this.properties.set(properties)

    this.zoomLevel.set(this.appState.getState().zoom)
    this.mapClusteredSignsDetails(properties)
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId,
    })
  }

  mapClusteredSignsDetails(properties: ClusteredSignProperties) {
    const items: DetailsItemModel[] = [
      {
        description: 'ID',
        value: this.selectedFeatureId(),
      },
      {
        description: 'DisplayType',
        value: properties.displayType!,
      },
      {
        description: 'Version',
        value: properties.version!.toString(),
      },
      {
        description: 'Submitter',
        value: properties.submitter!.toString(),
      },
      {
        description: 'MainType',
        value: properties.mainType.toString() ?? '',
      },
      {
        description: 'SpeedLimit',
        value: properties.speedLimit?.toString() ?? 'null',
      },

      {
        description: `CreationTime(${getTimeZoneLabel(new Date(properties.creationTime))})`,
        value: `${
          properties.creationTime
            ? format(new Date(properties.creationTime), 'yyyy-MM-dd HH:mm')
            : '-'
        }`,
      },
      {
        description: `UpdateTime(${getTimeZoneLabel(new Date(properties.updateTime))})`,
        value: `${
          properties.updateTime ? format(new Date(properties.updateTime), 'yyyy-MM-dd HH:mm') : '-'
        }`,
      },
      {
        description: 'Supplementary',
        value: JSON.stringify(properties.supplementalSign ?? '[]'),
      },
    ]

    // Only add Tags if they exist and are not empty
    if (Array.isArray(properties.tags) && properties.tags.length > 0) {
      items.push({
        description: 'Tags',
        value: properties.tags.join(', '),
      })
    }

    this.setDetail('Properties', {
      title: 'Clustered Sign',
      items: items,
    })
    this.setDetail('Sign', {
      title: 'Sign',
      items: this.getSignItem(properties),
    })
    this.setDetail('Supplementary Signs', {
      title: 'Supplementary Sign',
      items: this.getSupItems(properties.supplementalSign),
    })
  }

  getSupItems(supplementalSigns: SupplementalSign[]): DetailsItemModel[] {
    const items: DetailsItemModel[] = []
    supplementalSigns = supplementalSigns || []
    supplementalSigns.forEach((sign) => {
      if (sign.type === 'UNKNOWN_TYPE') {
        items.push({
          description: MapSupsIcons.SUP_UNKNOWN,
          value: sign.value ?? '',
        })
      } else {
        items.push({
          description: sign.type,
          value: sign.value ?? '',
        })
      }
    })
    return items
  }

  getSignItem(properties: ClusteredSignProperties): DetailsItemModel[] {
    let description = properties.mainType.includes(MapSignsIcons.SPEED_LIMIT)
      ? properties.mainType + '_' + (properties.speedLimit ?? 'NULL')
      : properties.mainType

    // Add _DIGITAL suffix if displayType is digital
    if (properties.displayType?.toLowerCase() === 'digital') {
      description += '_DIGITAL'
    }

    const item: DetailsItemModel = {
      description: description,
      value: properties.speedLimit?.toString() ?? '',
    }

    return [item]
  }
  getSupplementaryFontSize(value: string): string {
    // Adjust font size based on value
    const length = value?.toString().length || 0
    if (length >= 10) return '10px'
    return '16px'
  }
  close() {
    this.clearDetails()
    this.selectedFeatureId.set('')
    this.properties.set(undefined)
    this.features.set([])
    this.zoomLevel.set(undefined)
    this.index.set(0)
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: undefined,
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this.details.update((details) => new Map(details).set(key, value))
  }

  private clearDetails(): void {
    this.details.set(new Map())
  }
}
